import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { brl } from "@/lib/format";
import { Plus, Search, X, Pencil, Trash2, CheckCircle2, ExternalLink, RotateCcw, SlidersHorizontal, Download } from "lucide-react";

export const ISSUE_STATUS_META = {
    pendente: { label: "Pendente", bg: "#FEE2E2", color: "#B91C1C" },
    em_andamento: { label: "Em andamento", bg: "#DBEAFE", color: "#1D4ED8" },
    concluida: { label: "Concluída", bg: "#D1FAE5", color: "#047857" },
};

export const EMPTY_ISSUE_FILTERS = { search: "", status: "all", identificado: "all", tipo: "all", de: "", ate: "" };

export function filterIssues(issues, f) {
    return issues.filter(i => {
        if (f.search) {
            const q = f.search.toLowerCase();
            if (!(`${i.titulo} ${i.observacoes}`.toLowerCase().includes(q))) return false;
        }
        if (f.status !== "all" && i.status !== f.status) return false;
        if (f.identificado !== "all" && i.identificado !== f.identificado) return false;
        if (f.tipo !== "all" && i.tipo !== f.tipo) return false;
        if (f.de && (i.data || "") < f.de) return false;
        if (f.ate && (i.data || "") > f.ate) return false;
        return true;
    });
}

export function IssueStatusBadge({ status }) {
    const m = ISSUE_STATUS_META[status] || ISSUE_STATUS_META.pendente;
    return <span className="badge-status" style={{ background: m.bg, color: m.color }}>{m.label}</span>;
}

// M10 — exportação de Issues/Tasks em PDF (via janela de impressão, links clicáveis)
export function exportIssuesPDF(issues, { title = "Issues / Tasks", showCliente = true } = {}) {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const fmtData = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—");
    const geradoEm = new Date().toLocaleString("pt-BR");

    // Agrupar por cliente/contrato
    const groups = {};
    issues.forEach((i) => {
        const key = i.organizacao_nome || i.contract_id || "—";
        (groups[key] = groups[key] || []).push(i);
    });
    const groupNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    const linkCell = (i) => {
        const links = [];
        if (i.link_issue) links.push(`<a href="${esc(i.link_issue)}">Issue</a>`);
        if (i.link_task) links.push(`<a href="${esc(i.link_task)}">Task</a>`);
        return links.length ? links.join(" · ") : "—";
    };

    const sections = groupNames.map((gn) => {
        const rows = groups[gn].map((i) => `
            <tr>
                <td>${fmtData(i.data)}</td>
                <td>${esc(i.identificado)}</td>
                <td><span class="tipo">${esc(i.tipo)}</span> ${esc(i.titulo)}</td>
                <td>${esc((ISSUE_STATUS_META[i.status] || ISSUE_STATUS_META.pendente).label)}</td>
                <td>${esc(i.observacoes) || "—"}</td>
                <td class="lnk">${linkCell(i)}</td>
            </tr>`).join("");
        return `
            <h2>${showCliente ? esc(gn) : ""}</h2>
            <table>
                <thead><tr>
                    <th style="width:80px">Data</th>
                    <th style="width:78px">Identificado</th>
                    <th>Descrição da Issue/Task</th>
                    <th style="width:90px">Status</th>
                    <th>Observação</th>
                    <th style="width:90px">Link</th>
                </tr></thead>
                <tbody>${rows}</tbody>
            </table>`;
    }).join("");

    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${esc(title)}</title>
    <style>
        * { box-sizing: border-box; }
        body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #0F2044; margin: 28px; }
        header { border-bottom: 2px solid #0F2044; padding-bottom: 10px; margin-bottom: 18px; }
        header .kicker { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #64748b; font-weight: 700; }
        header h1 { font-size: 20px; margin: 4px 0 2px; }
        header .meta { font-size: 11px; color: #64748b; }
        h2 { font-size: 13px; margin: 18px 0 6px; color: #0F2044; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        th, td { border: 1px solid #e2e8f0; padding: 6px 8px; font-size: 11px; text-align: left; vertical-align: top; word-break: break-word; }
        th { background: #f1f5f9; text-transform: uppercase; letter-spacing: .06em; font-size: 9.5px; color: #475569; }
        .tipo { display: inline-block; background: #e2e8f0; color: #334155; border-radius: 4px; padding: 0 5px; font-size: 8.5px; text-transform: uppercase; margin-right: 4px; }
        .lnk a { color: #0F2044; }
        @media print { body { margin: 12mm; } h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
    </style></head><body>
        <header>
            <div class="kicker">Customer Success · licito.guru</div>
            <h1>${esc(title)}</h1>
            <div class="meta">${issues.length} registro(s) · Gerado em ${esc(geradoEm)}</div>
        </header>
        ${issues.length === 0 ? "<p>Nenhum registro para exportar.</p>" : sections}
        <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
    </body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Habilite pop-ups para exportar o PDF."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
}

// Item 2 (novo lote) — exportação de Issues/Tasks em Excel (.xlsx) via backend (openpyxl)
export async function exportIssuesXLSX(issues, { title } = {}) {
    const rows = issues.map(i => ({
        organizacao_nome: i.organizacao_nome || "—",
        data: i.data || "",
        identificado: i.identificado || "",
        tipo: i.tipo || "",
        titulo: i.titulo || "",
        status: i.status || "",
        observacoes: i.observacoes || "",
        link_issue: i.link_issue || "",
        link_task: i.link_task || "",
    }));
    const res = await api.post("/issues/export-xlsx", { rows, title: title || "Issues / Tasks" }, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "issues_tasks.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

// Barra de filtros compartilhada (busca, status, identificado, avançados, limpar, nova)
export function IssueFilters({ filters, onChange, onNew, onExport, onExportXlsx, testidPrefix = "issue" }) {
    const [advanced, setAdvanced] = useState(false);
    const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_ISSUE_FILTERS);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={filters.search} onChange={(e) => onChange({ ...filters, search: e.target.value })}
                        placeholder="Buscar por issue/task ou observação…"
                        data-testid={`${testidPrefix}-search`}
                        className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none" />
                </div>
                <select value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}
                    data-testid={`${testidPrefix}-filter-status`}
                    className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:outline-none">
                    <option value="all">Status: todos</option>
                    {Object.entries(ISSUE_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={filters.identificado} onChange={(e) => onChange({ ...filters, identificado: e.target.value })}
                    data-testid={`${testidPrefix}-filter-identificado`}
                    className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:outline-none">
                    <option value="all">Identificado: todos</option>
                    <option value="cliente">Cliente</option>
                    <option value="interno">Interno</option>
                </select>
                <button onClick={() => setAdvanced(a => !a)} data-testid={`${testidPrefix}-advanced-toggle`}
                    className={`text-sm px-3 py-2 rounded-md border inline-flex items-center gap-1.5 ${advanced ? "border-[#0F2044] text-[#0F2044] bg-[#0F2044]/5" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                    <SlidersHorizontal className="h-3.5 w-3.5" /> Filtros avançados
                </button>
                {hasFilters && (
                    <button onClick={() => onChange({ ...EMPTY_ISSUE_FILTERS })} data-testid={`${testidPrefix}-clear-filters`}
                        className="text-sm px-3 py-2 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1">
                        <X className="h-3.5 w-3.5" /> Limpar filtros
                    </button>
                )}
                <button onClick={onNew} data-testid={`${testidPrefix}-new-button`}
                    className="ml-auto text-sm font-semibold bg-[#0F2044] hover:bg-[#172E5E] text-white px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                    <Plus className="h-4 w-4" /> Nova issue/task
                </button>
                {onExport && (
                    <button onClick={onExport} data-testid={`${testidPrefix}-export-pdf`}
                        className="text-sm font-semibold border border-[#0F2044] text-[#0F2044] hover:bg-[#0F2044]/5 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                        <Download className="h-4 w-4" /> Exportar PDF
                    </button>
                )}
                {onExportXlsx && (
                    <button onClick={onExportXlsx} data-testid={`${testidPrefix}-export-xlsx`}
                        className="text-sm font-semibold border border-emerald-600 text-emerald-700 hover:bg-emerald-50 px-4 py-2 rounded-md inline-flex items-center gap-1.5">
                        <Download className="h-4 w-4" /> Exportar Excel
                    </button>
                )}
            </div>
            {advanced && (
                <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5" data-testid={`${testidPrefix}-advanced-panel`}>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Período — de</label>
                        <input type="date" value={filters.de} onChange={(e) => onChange({ ...filters, de: e.target.value })}
                            data-testid={`${testidPrefix}-filter-de`} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Até</label>
                        <input type="date" value={filters.ate} onChange={(e) => onChange({ ...filters, ate: e.target.value })}
                            data-testid={`${testidPrefix}-filter-ate`} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Tipo</label>
                        <select value={filters.tipo} onChange={(e) => onChange({ ...filters, tipo: e.target.value })}
                            data-testid={`${testidPrefix}-filter-tipo`} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
                            <option value="all">Todos</option>
                            <option value="issue">Issue</option>
                            <option value="task">Task</option>
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
}

// Tabela em formato de planilha
export function IssueTable({ issues, showCliente = false, onEdit, onDelete, onStatusChange, emptyText = "Nenhum registro." }) {
    if (issues.length === 0) return <div className="text-sm text-slate-500 py-4 text-center">{emptyText}</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-200">
                        {showCliente && <th className="py-2 pr-3 font-medium">Cliente</th>}
                        <th className="py-2 pr-3 font-medium">Data</th>
                        <th className="py-2 pr-3 font-medium">Identificado</th>
                        <th className="py-2 pr-3 font-medium">Issue/Task</th>
                        <th className="py-2 pr-3 font-medium">Status</th>
                        <th className="py-2 pr-3 font-medium">Observações</th>
                        <th className="py-2 pr-3 font-medium">Link</th>
                        <th className="py-2 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {issues.map(i => (
                        <tr key={i.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`issue-row-${i.id}`}>
                            {showCliente && <td className="py-2.5 pr-3 font-medium text-[#0F2044] whitespace-nowrap">{i.organizacao_nome}</td>}
                            <td className="py-2.5 pr-3 font-mono-tab text-slate-600 whitespace-nowrap">{brl(i.data)}</td>
                            <td className="py-2.5 pr-3 capitalize text-slate-600">{i.identificado}</td>
                            <td className="py-2.5 pr-3">
                                <span className="badge-status badge-neutral mr-1.5 uppercase text-[9px]">{i.tipo}</span>
                                <span className={`font-medium ${i.status === "concluida" ? "text-slate-400" : "text-[#0F2044]"}`}>{i.titulo}</span>
                            </td>
                            <td className="py-2.5 pr-3"><IssueStatusBadge status={i.status} /></td>
                            <td className="py-2.5 pr-3 text-slate-600 max-w-[220px]">
                                <div className="truncate" title={i.observacoes}>{i.observacoes || "—"}</div>
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                                {i.link_issue && (
                                    <a href={i.link_issue} target="_blank" rel="noreferrer" data-testid={`issue-link-issue-${i.id}`}
                                        className="inline-flex items-center gap-1 text-xs text-[#0F2044] hover:underline mr-2">
                                        <ExternalLink className="h-3 w-3" /> Issue
                                    </a>
                                )}
                                {i.link_task && (
                                    <a href={i.link_task} target="_blank" rel="noreferrer" data-testid={`issue-link-task-${i.id}`}
                                        className="inline-flex items-center gap-1 text-xs text-violet-700 hover:underline">
                                        <ExternalLink className="h-3 w-3" /> Task
                                    </a>
                                )}
                                {!i.link_issue && !i.link_task && <span className="text-slate-300">—</span>}
                            </td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                                {i.status !== "concluida" ? (
                                    <button onClick={() => onStatusChange(i, "concluida")} data-testid={`issue-conclude-${i.id}`}
                                        title="Concluir" className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50">
                                        <CheckCircle2 className="h-4 w-4" />
                                    </button>
                                ) : (
                                    <button onClick={() => onStatusChange(i, "pendente")} data-testid={`issue-reopen-${i.id}`}
                                        title="Reabrir" className="p-1.5 rounded text-slate-400 hover:bg-slate-100">
                                        <RotateCcw className="h-4 w-4" />
                                    </button>
                                )}
                                <button onClick={() => onEdit(i)} data-testid={`issue-edit-${i.id}`}
                                    title="Editar" className="p-1.5 rounded text-slate-400 hover:text-[#0F2044] hover:bg-slate-100">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => onDelete(i)} data-testid={`issue-delete-${i.id}`}
                                    title="Excluir" className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Modal de cadastro/edição — quando contractId é passado, contrato/cliente vêm pré-preenchidos
export function IssueFormModal({ contractId, contractName, issue, onClose, onSaved }) {
    const editing = Boolean(issue);
    const [form, setForm] = useState({
        data: issue?.data || new Date().toISOString().slice(0, 10),
        identificado: issue?.identificado || "interno",
        titulo: issue?.titulo || "",
        tipo: issue?.tipo || "issue",
        status: issue?.status || "pendente",
        observacoes: issue?.observacoes || "",
        link_issue: issue?.link_issue || "",
        link_task: issue?.link_task || "",
    });
    const [selectedContract, setSelectedContract] = useState(contractId || issue?.contract_id || "");
    const [contracts, setContracts] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const needsContractSelect = !contractId && !editing;

    useEffect(() => {
        if (needsContractSelect) {
            api.get("/contracts").then(r => setContracts(r.data));
        }
    }, [needsContractSelect]);

    async function submit() {
        if (!form.titulo.trim()) { setError("Informe o título da issue/task."); return; }
        const cid = editing ? issue.contract_id : selectedContract;
        if (!cid) { setError("Selecione o contrato vinculado."); return; }
        setSaving(true); setError("");
        try {
            if (editing) {
                await api.patch(`/issues/${issue.id}`, { ...form, titulo: form.titulo.trim() });
            } else {
                await api.post(`/contracts/${cid}/issues`, { ...form, titulo: form.titulo.trim() });
            }
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Erro ao salvar a issue/task.");
            setSaving(false);
        }
    }

    const inputCls = "w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none";
    const clienteLabel = contractName || issue?.organizacao_nome ||
        (contracts && selectedContract ? contracts.find(c => c.id === selectedContract)?.organizacao_nome : "");

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 overflow-y-auto py-6" data-testid="issue-form-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6 fade-in my-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#0F2044] font-semibold">Issues / Tasks</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">{editing ? "Editar registro" : "Nova issue/task"}</h3>
                    </div>
                    <button onClick={onClose} data-testid="issue-form-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Contrato vinculado</label>
                            {needsContractSelect ? (
                                <select value={selectedContract} onChange={(e) => setSelectedContract(e.target.value)}
                                    data-testid="issue-contract-select" className={`${inputCls} bg-white`}>
                                    <option value="">Selecione…</option>
                                    {(contracts || []).map(c => <option key={c.id} value={c.id}>{c.organizacao_nome}</option>)}
                                </select>
                            ) : (
                                <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="issue-contract-fixed">
                                    {clienteLabel || "—"}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Cliente</label>
                            <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-500" data-testid="issue-cliente-auto">
                                {clienteLabel || "Preenchido automaticamente"}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Data</label>
                            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                                data-testid="issue-data-input" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Identificado</label>
                            <select value={form.identificado} onChange={(e) => setForm({ ...form, identificado: e.target.value })}
                                data-testid="issue-identificado-select" className={`${inputCls} bg-white`}>
                                <option value="interno">Interno</option>
                                <option value="cliente">Cliente</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Tipo</label>
                            <div className="flex rounded-md border border-slate-300 overflow-hidden">
                                <button type="button" onClick={() => setForm({ ...form, tipo: "issue" })} data-testid="issue-tipo-issue"
                                    className={`flex-1 py-2 text-sm font-semibold ${form.tipo === "issue" ? "bg-[#0F2044] text-white" : "bg-white text-slate-600"}`}>
                                    Issue
                                </button>
                                <button type="button" onClick={() => setForm({ ...form, tipo: "task" })} data-testid="issue-tipo-task"
                                    className={`flex-1 py-2 text-sm font-semibold ${form.tipo === "task" ? "bg-violet-700 text-white" : "bg-white text-slate-600"}`}>
                                    Task
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Issue / Task *</label>
                        <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                            placeholder="Título ou descrição objetiva do problema/atividade"
                            data-testid="issue-titulo-input" className={inputCls} autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Status</label>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                                data-testid="issue-status-select" className={`${inputCls} bg-white`}>
                                {Object.entries(ISSUE_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Observações</label>
                            <input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                                placeholder="Campo livre" data-testid="issue-observacoes-input" className={inputCls} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Link da Issue (Notion)</label>
                            <input value={form.link_issue} onChange={(e) => setForm({ ...form, link_issue: e.target.value })}
                                placeholder="https://…" data-testid="issue-link-issue-input" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-1">Link da Task (Notion)</label>
                            <input value={form.link_task} onChange={(e) => setForm({ ...form, link_task: e.target.value })}
                                placeholder="https://…" data-testid="issue-link-task-input" className={inputCls} />
                        </div>
                    </div>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="issue-form-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="issue-form-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="issue-form-save"
                        className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold disabled:opacity-60">
                        {saving ? "Salvando…" : editing ? "Salvar alterações" : "Cadastrar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Aba Issues/Tasks dentro do contrato (M7) — só os registros do contrato aberto
export default function IssuesTab({ contract }) {
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ ...EMPTY_ISSUE_FILTERS });
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    async function reload() {
        setLoading(true);
        try {
            const r = await api.get(`/contracts/${contract.id}/issues`);
            setIssues(r.data);
        } finally { setLoading(false); }
    }
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [contract.id]);

    async function setStatus(i, status) {
        await api.patch(`/issues/${i.id}`, { status });
        await reload();
    }
    async function remove(i) {
        if (!window.confirm(`Excluir a ${i.tipo} "${i.titulo}"?\nEsta ação não pode ser desfeita.`)) return;
        await api.delete(`/issues/${i.id}`);
        await reload();
    }

    const filtered = useMemo(() => filterIssues(issues, filters), [issues, filters]);
    const emAndamento = filtered.filter(i => i.status !== "concluida");
    const concluidas = filtered.filter(i => i.status === "concluida");

    return (
        <div className="space-y-6" data-testid="issues-tab">
            <IssueFilters filters={filters} onChange={setFilters} onNew={() => { setEditing(null); setFormOpen(true); }}
                onExport={() => exportIssuesPDF(filtered, { title: `Issues / Tasks — ${contract.organizacao_nome}`, showCliente: false })}
                onExportXlsx={() => exportIssuesXLSX(filtered.map(i => ({ ...i, organizacao_nome: contract.organizacao_nome })), { title: `Issues / Tasks — ${contract.organizacao_nome}` })} />
            {loading ? (
                <div className="text-sm text-slate-500 py-6 text-center">Carregando…</div>
            ) : (
                <>
                    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="issues-em-andamento">
                        <h3 className="font-display font-semibold text-base text-[#0F2044] mb-3">Em andamento ({emAndamento.length})</h3>
                        <IssueTable issues={emAndamento} onEdit={(i) => { setEditing(i); setFormOpen(true); }}
                            onDelete={remove} onStatusChange={setStatus}
                            emptyText="Nenhuma issue/task em andamento para este contrato." />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="issues-concluidas">
                        <h3 className="font-display font-semibold text-base text-slate-500 mb-3">Concluídas ({concluidas.length})</h3>
                        <IssueTable issues={concluidas} onEdit={(i) => { setEditing(i); setFormOpen(true); }}
                            onDelete={remove} onStatusChange={setStatus}
                            emptyText="Nenhuma issue/task concluída ainda." />
                    </div>
                </>
            )}
            {formOpen && (
                <IssueFormModal contractId={contract.id} contractName={contract.organizacao_nome} issue={editing}
                    onClose={() => setFormOpen(false)}
                    onSaved={async () => { setFormOpen(false); setEditing(null); await reload(); }} />
            )}
        </div>
    );
}
